import { Subject } from 'rxjs/Subject'
import { Observable } from 'rxjs/Observable'

import { IKeyStrategy } from './key-strategy.interface'
import { PrefixKeyStrategy } from './prefix-key-strategy'

interface IPropertyChangeValues {
  oldValue: any
  value: any
}

interface IPropertyChanges {
  [key: string]: IPropertyChangeValues
}

interface IDataStore<K, V> {
  get: (key: K) => V
  set: (key: K, value: V) => void
  delete: (key: K) => void
  keys: () => Iterable<K>
}

export class AnalyticsDataProvider {

  private static _instance: AnalyticsDataProvider

  private static _namespace: string = AnalyticsDataProvider.name

  public static get instance() {
    if (!AnalyticsDataProvider._instance) {
      AnalyticsDataProvider._instance = new AnalyticsDataProvider(
        new Map<string, any>(),
        new PrefixKeyStrategy(AnalyticsDataProvider.name)
      )
    }
    return AnalyticsDataProvider._instance
  }

  private _setPropertyObserver: Subject<IPropertyChanges> = new Subject<IPropertyChanges>()

  private constructor(
    private _dataStore: IDataStore<string, any>,
    private _keyStrategy: IKeyStrategy
  ) {}

  public set(propertyName: string, value: any) {
    const key = this._keyStrategy.getKey(propertyName)
    const oldValue = this._dataStore.get(key)
    this._dataStore.set(key, value)

    const changeSet = {}
    changeSet[propertyName] = this._createPropertyChangeValues(oldValue, value)

    this._emitSetEvent(changeSet)
  }

  public get(propertyName: string): any {
    const value = this._dataStore.get(this._keyStrategy.getKey(propertyName))
    return value
  }

  public delete(propertyName: string) {
    this._dataStore.delete(this._keyStrategy.getKey(propertyName))
  }

  public props(): string[] {
    return Array.from(this._dataStore.keys())
  }

  public reset() {
    this._removeOwnKeysFromStore()
    this._restartObserver()
  }

  public exposeToWindow() {
    if (! window) { return }
    const globalName = AnalyticsDataProvider._namespace

    const windowEventEmitter = (eventName: string) => (change) => {
      const event = new Event(eventName)
      event['detail'] = change
      window.dispatchEvent(event)
    }

    this.onSet().subscribe(windowEventEmitter(this._eventName('onSet')))
    this.onChanges().do((change) => {
      Object.keys(change)
        .filter((propertyName) => this._hasPropertyChanged(change[propertyName]))
        .forEach((propertyName) => windowEventEmitter(this._eventName('onPropertyChange', propertyName))(change))
    }).subscribe(windowEventEmitter(this._eventName('onChanges')))

    window[globalName] = this
    return globalName
  }

  public onSet(): Observable<IPropertyChanges> {
    return this._setPropertyObserver as Observable<IPropertyChanges>
  }

  public onChanges(): Observable <IPropertyChanges> {
    return this.onSet().filter((changeSet) => {
      return Object.keys(changeSet).some((changedPropertyName) => {
        return this._hasPropertyChanged(changeSet[changedPropertyName])
      })
    })
  }

  public onPropertyChange(propertyName: string): Observable <IPropertyChanges> {
    return this.onChanges().filter((changeSet: IPropertyChanges) => {
      return propertyName in changeSet && this._hasPropertyChanged(changeSet[propertyName])
    }) as Observable<IPropertyChanges>
  }

  private _emitSetEvent(changeSet: IPropertyChanges) {
    this._setPropertyObserver.next(changeSet)
  }

  private _createPropertyChangeValues(oldValue, newValue): IPropertyChangeValues {
    return {
      value: newValue,
      oldValue: oldValue
    }
  }

  private _hasPropertyChanged(changeValues: IPropertyChangeValues): boolean {
    return changeValues.oldValue !== changeValues.value
  }

  private _removeOwnKeysFromStore() {
    const keys  = Array.from(this._dataStore.keys())
    keys.forEach((key) => {
      if (this._keyStrategy.isKey(key)) {
        this._dataStore.delete(key)
      }
    })
  }

  private _restartObserver() {
    this._setPropertyObserver.complete()
    this._setPropertyObserver = new Subject<IPropertyChanges>()
  }

  private _eventName(eventName: string, ...subEvents: string[]) {
    const eventParts = [AnalyticsDataProvider._namespace, eventName].concat(subEvents)
    return eventParts.join('::')
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/helpers/analytics-data-provider/analytics-data-provider.ts